/**
 * Meridian Registry — Design System Tokens
 *
 * This is a drop-in replacement for the original theme.ts.
 *
 * Backwards compatibility:
 *   - theme.bg.*         — kept (also available as theme.surface.*)
 *   - theme.border       — kept as string (also available as theme.borderColor.default)
 *   - theme.text.*       — kept + extended (added tertiary, quaternary, inverse)
 *   - theme.accent.*     — kept (also available as theme.brand.* and theme.semantic.*)
 *   - theme.cards.*      — kept but @deprecated (use theme.semantic.* instead)
 *   - theme.fonts.*      — kept (also available as theme.font.*)
 *
 * New tokens:
 *   - theme.surface      — layered surface system
 *   - theme.borderColor  — multiple border weights
 *   - theme.brand        — cyan scale + glow tokens
 *   - theme.semantic     — success/warning/danger/info with soft + border variants
 *   - theme.space        — 4pt spacing scale
 *   - theme.radius       — border radius scale
 *   - theme.shadow       — shadow + glow tokens
 *   - theme.motion       — transition timings
 *   - theme.fontSize     — type scale
 *   - theme.fontWeight   — weights
 *   - theme.letterSpacing— tracking scale
 *   - theme.z            — z-index layers
 */

export const theme = {
  /* ═══════════════ SURFACES ═══════════════ */
  /* Layered from deepest (page) to brightest (elevated).
     Each step ~4% brighter. Use to create depth without borders. */
  surface: {
    base:      '#080C18', // page background
    panel:     '#0B0F1F', // main content panels / sidebar
    card:      '#0F1628', // cards on panels
    cardHover: '#141C33',
    elevated:  '#1A2340', // modals, dropdowns, popovers
    inset:     '#060913', // deeper than base — sunken inputs
  },

  /* ═══════════════ BORDERS ═══════════════ */
  borderColor: {
    subtle:  'rgba(148, 163, 184, 0.08)', // dividers
    default: 'rgba(148, 163, 184, 0.12)', // cards, inputs
    strong:  'rgba(148, 163, 184, 0.20)', // hover, focus
    brand:   'rgba(0, 212, 255, 0.30)',   // active
  },

  /* ═══════════════ TEXT ═══════════════ */
  text: {
    primary:    '#E8EFF8',
    secondary:  '#94A3B8', // slightly brighter than original #5A7090 for better contrast
    tertiary:   '#64748B',
    quaternary: '#475569',
    inverse:    '#000000',
  },

  /* ═══════════════ BRAND ═══════════════ */
  brand: {
    cyan:      '#00D4FF',
    cyanLight: '#7DE5F5',
    cyanDark:  '#0099CC',
    cyanSoft:  'rgba(0, 212, 255, 0.10)',
    cyanGlow:  'rgba(0, 212, 255, 0.35)',
    cyanGlowSoft: 'rgba(0, 212, 255, 0.15)',
  },

  /* ═══════════════ SEMANTIC ═══════════════ */
  semantic: {
    success:       '#10B981',
    successSoft:   'rgba(16, 185, 129, 0.10)',
    successBorder: 'rgba(16, 185, 129, 0.30)',

    warning:       '#F59E0B',
    warningSoft:   'rgba(245, 158, 11, 0.10)',
    warningBorder: 'rgba(245, 158, 11, 0.30)',

    danger:        '#EF4444',
    dangerSoft:    'rgba(239, 68, 68, 0.10)',
    dangerBorder:  'rgba(239, 68, 68, 0.30)',

    info:          '#00D4FF',
    infoSoft:      'rgba(0, 212, 255, 0.10)',
    infoBorder:    'rgba(0, 212, 255, 0.30)',
  },

  /* ═══════════════ TYPE ═══════════════ */
  font: {
    ui:      "'DM Sans', -apple-system, system-ui, sans-serif",
    data:    "'Space Mono', 'SF Mono', ui-monospace, monospace",
    display: "'DM Sans', -apple-system, system-ui, sans-serif",
  },

  fontSize: {
    xs:   '11px',
    sm:   '12px',
    base: '13px',
    md:   '14px',
    lg:   '16px',
    xl:   '20px',
    '2xl':'26px',
    '3xl':'36px',
    '4xl':'48px',
  },

  fontWeight: {
    regular:  400,
    medium:   500,
    semibold: 600,
    bold:     700,
  },

  letterSpacing: {
    tight:    '-0.03em',
    tightish: '-0.02em',
    normal:   '-0.005em',
    wide:     '0.1em',
    wider:    '0.15em',
    widest:   '0.2em',
  },

  /* ═══════════════ SPACING (4pt) ═══════════════ */
  space: {
    0:  '0',
    1:  '4px',
    2:  '8px',
    3:  '12px',
    4:  '16px',
    5:  '20px',
    6:  '24px',
    8:  '32px',
    10: '40px',
    12: '48px',
    16: '64px',
    20: '80px',
  },

  /* ═══════════════ RADIUS ═══════════════ */
  radius: {
    sm:   '6px',
    md:   '10px',
    lg:   '14px',
    xl:   '20px',
    pill: '999px',
  },

  /* ═══════════════ SHADOWS ═══════════════ */
  shadow: {
    card:     '0 1px 0 0 rgba(255,255,255,0.03) inset, 0 1px 2px rgba(0,0,0,0.4)',
    elevated: '0 8px 24px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',
    modal:    '0 24px 48px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)',
    glow:     '0 0 0 1px rgba(0,212,255,0.35), 0 0 40px -8px rgba(0,212,255,0.35)',
    glowSoft: '0 0 20px -4px rgba(0,212,255,0.25)',
  },

  /* ═══════════════ MOTION ═══════════════ */
  motion: {
    fast:   '120ms ease',
    base:   '200ms ease',
    slow:   '320ms ease',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },

  /* ═══════════════ Z-INDEX ═══════════════ */
  z: {
    base:     1,
    nav:      100,
    dropdown: 150,
    drawer:   200,
    modal:    300,
    toast:    400,
  },

  /* ═══════════════ LEGACY (kept for backwards compat) ═══════════════ */
  bg: {
    dark:    '#080C18',
    sidebar: '#0B0F1F',
    card:    '#0F1628',
    cardAlt: '#0A1020',
  },
  border: 'rgba(148, 163, 184, 0.12)', // was '#1A2540' — switched to translucent for better layering
  accent: {
    cyan:  '#00D4FF',
    amber: '#F59E0B',
    green: '#10B981', // was '#00C896' — aligned to semantic.success
    red:   '#EF4444', // was '#FF4560' — aligned to semantic.danger
  },
  fonts: {
    ui:   "'DM Sans', sans-serif",
    data: "'Space Mono', monospace",
  },
  /** @deprecated Use theme.semantic.* instead. Kept for backwards compat during migration. */
  cards: {
    outlets:       { bg: 'rgba(236,72,153,0.10)',  border: 'rgba(236,72,153,0.30)'  },
    confirmed:     { bg: 'rgba(16,185,129,0.10)',  border: 'rgba(16,185,129,0.30)'  },
    pending:       { bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.30)'  },
    clients:       { bg: 'rgba(139,92,246,0.10)',  border: 'rgba(139,92,246,0.30)'  },
    deterministic: { bg: 'rgba(0,212,255,0.10)',   border: 'rgba(0,212,255,0.30)'   },
    fuzzy:         { bg: 'rgba(249,115,22,0.10)',  border: 'rgba(249,115,22,0.30)'  },
    total:         { bg: 'rgba(99,102,241,0.10)',  border: 'rgba(99,102,241,0.30)'  },
  },
} as const;

export type Theme = typeof theme;
